import { PlusOutlined } from '@ant-design/icons';
import { AuthSubscriptions } from '@hefe/auth';
import { t } from '@hefe/i18n';
import {
  BasketballClubGame,
  BasketballGame,
  EventReportEventableType,
  EventReportGroupableType,
} from '@hefe/models';
import {
  currentUserIsAdminSelector,
  currentUserSelector,
  ModalsSlice,
} from '@hefe/store';
import { clubHasSubscription, useUserBasketballGameRoles } from '@hefe/utils';
import { Button, Modal, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import AddEditEventReportForm from '../../forms/add-edit-event-report/add-edit-event-report.form';
import GenerateEventReportWizardComponent from '../generate-event-report-wizard/generate-event-report-wizard/generate-event-report-wizard.component';

interface NewEventReportButtonComponentProps {
  game: BasketballGame;
  clubDetails?: BasketballClubGame | null;
}

export default function NewEventReportButtonComponent(
  props: NewEventReportButtonComponentProps
) {
  const { clubDetails, game } = props;

  const dispatch = useAppDispatch();
  const user = useSelector(currentUserSelector);
  const userIsAdmin = useSelector(currentUserIsAdminSelector);
  const hasSubscription = clubHasSubscription(AuthSubscriptions.third, user);

  const { userIsCoach } = useUserBasketballGameRoles({ game, user });

  const [modalOpen, setModalOpen] = useState(false);

  const openAddReportForm = () => {
    if (!clubDetails) {
      return;
    }

    setModalOpen(false);

    dispatch(
      ModalsSlice.modalsActions.openModal({
        formId: 'add-edit-event-report-form-new',
        component: (
          <AddEditEventReportForm
            game={game}
            eventId={clubDetails.id}
            eventType={EventReportEventableType.BASKETBALL_CLUB_GAME}
            groupType={EventReportGroupableType.CLUB}
            groupId={clubDetails.club_id}
          />
        ),
        title: t('game_reports.add'),
        width: 800,
      })
    );
  };

  const openGenerateReportModal = () => {
    if (!clubDetails) {
      return;
    }

    setModalOpen(false);

    dispatch(
      ModalsSlice.modalsActions.openModal({
        formId: 'generate-event-report-modal',
        component: (
          <GenerateEventReportWizardComponent
            game={game}
            gameId={game.id}
            eventId={clubDetails.id}
            eventType={EventReportEventableType.BASKETBALL_CLUB_GAME}
            groupType={EventReportGroupableType.CLUB}
            groupId={clubDetails.club_id}
          />
        ),
        title: t('game_reports.generate.wizard.title'),
        width: 800,
        hideCancelButton: true,
        hideOkButton: true,
      })
    );
  };

  const userIsAdminOrCoach = useMemo(() => {
    return userIsAdmin || userIsCoach;
  }, [userIsAdmin, userIsCoach]);

  if (!userIsAdminOrCoach) {
    return null;
  }

  return (
    <>
      <Button icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
        {t('game_reports.new_game_report_button.title')}
      </Button>
      <Modal
        open={modalOpen}
        // verfassen oder generieren
        title={t('game_reports.new_game_report_button.title')}
        footer={[
          <Button
            type="primary"
            onClick={() => openGenerateReportModal()}
            disabled={!hasSubscription}
          >
            {t('game_reports.new_game_report_button.generate')}
          </Button>,
          <Button onClick={() => openAddReportForm()}>
            {t('game_reports.new_game_report_button.write')}
          </Button>,
          <Button danger onClick={() => setModalOpen(false)}>
            {t('game_reports.new_game_report_button.cancel')}
          </Button>,
        ]}
        onCancel={() => setModalOpen(false)}
      >
        <Typography.Text>
          {t('game_reports.new_game_report_button.description.question')}
          {!hasSubscription
            ? t('game_reports.new_game_report_button.description.upgrade')
            : ''}
        </Typography.Text>
      </Modal>
    </>
  );
}
